import React, { useState } from "react";

import { ReactComponent as SearchIcon } from "../../assets/icons/search-sm.svg";
import { ReactComponent as CloseIcon } from "../../assets/icons/x-close.svg";
import { ReactComponent as FilterIcon } from "../../assets/icons/filter-lines.svg";

import Select, { SelectOption } from "./Select";
import { Skeleton } from "../Skeleton";
import useIsMobile from "../../hooks/useIsMobile";

export interface FilterBarFilter {
  /** Wires the label to its trigger, so it must be unique on the page. */
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  /** The "no filter" row, always first in the list. */
  allLabel?: string;
  /** Tailwind width for the field above `sm`; full width below it. */
  widthClassName?: string;
}

export interface FilterBarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchId?: string;
  searchLabel?: string;
  searchPlaceholder?: string;
  filters?: FilterBarFilter[];
  /** Sort is ModelsToolbar's control, offered here so list pages share one anatomy. All three sort props must be passed together. */
  sortBy?: string;
  onSortChange?: (value: string) => void;
  sortOptions?: SelectOption[];
  /** Resets search and every filter. Rendered disabled rather than hidden — see below. */
  onClear: () => void;
  isFiltered: boolean;
  /** Phone-only controls shown at the top of the Filters panel — the page's own seg row, hidden below sm. */
  mobileControls?: React.ReactNode;
  /** Resets mobileControls. Phone-only: above sm those controls live outside the bar, so Clear must leave them alone. */
  onClearMobile?: () => void;
  /** How many of mobileControls are off their default, added to the Filters badge. */
  mobileActiveCount?: number;
  /** Pins the bar to the top of the page scroll so results run under it. See the note below. */
  sticky?: boolean;
  className?: string;
}

// Opaque (/95 leaked content); eai-bleed-x spans the shell gutters and eai-sticky-shell-top reads the shell's own top padding
export const STICKY_CLASSES =
  "sticky eai-sticky-shell-top z-20 eai-bleed-x bg-eai-surface-bright py-2 sm:py-3";

const FilterBar = ({
  searchValue,
  onSearchChange,
  searchId = "filterbar-search",
  searchLabel = "Search",
  searchPlaceholder = "Search…",
  filters = [],
  sortBy,
  onSortChange,
  sortOptions,
  onClear,
  isFiltered,
  mobileControls,
  onClearMobile,
  mobileActiveCount = 0,
  sticky = false,
  className = "",
}: FilterBarProps) => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Below sm the page's own controls fold into this panel, so Clear owns them too; above it they sit outside the bar and Clear must not touch them
  const isMobile = useIsMobile(640);
  const activeCount = filters.filter((filter) => Boolean(filter.value)).length + mobileActiveCount;
  const hasSort = Boolean(sortOptions?.length && onSortChange);
  const canClear = isFiltered || (isMobile && mobileActiveCount > 0);

  const handleClear = () => {
    onClear();
    if (isMobile) onClearMobile?.();
  };

  return (
    <div
      data-testid="filter-bar"
      data-sticky={sticky ? "true" : "false"}
      className={`eai-gap-toolbar ${sticky ? STICKY_CLASSES : ""} ${className}`}
    >
      <div className="eai-panel">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-0 flex-1 basis-48">
            <label htmlFor={searchId} className="eai-meta-label mb-1.5 block">
              {searchLabel}
            </label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                <SearchIcon className="h-4 w-4 text-eai-ink-faint" />
              </div>
              <input
                id={searchId}
                type="text"
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={searchPlaceholder}
                className="eai-input pl-10 pr-10"
              />
              {searchValue && (
                <button
                  type="button"
                  onClick={() => onSearchChange("")}
                  aria-label="Clear search"
                  className="group absolute inset-y-0 right-0 flex items-center pr-3"
                >
                  <div className="rounded-full p-1 transition-colors group-hover:bg-eai-surface-hover group-active:bg-eai-surface-active">
                    <CloseIcon className="h-4 w-4 text-eai-ink-faint group-hover:text-eai-ink [&_path]:stroke-current" />
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* Phone-only opener for the row below; the badge keeps hidden filters visible */}
          {(filters.length > 0 || hasSort || mobileControls) && (
            <button
              type="button"
              onClick={() => setFiltersOpen((open) => !open)}
              aria-expanded={filtersOpen}
              aria-controls={`${searchId}-filters`}
              data-testid="filter-bar-toggle"
              className="eai-btn-outline shrink-0 px-4 sm:hidden"
            >
              <FilterIcon className="h-4 w-4 shrink-0 [&_path]:stroke-current" />
              Filters
              {activeCount > 0 && (
                <span className="ml-0.5 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-eai-cta-primary px-1.5 font-outfit text-eai-caption text-white">
                  {activeCount}
                </span>
              )}
            </button>
          )}

          {/* sm:contents dissolves this wrapper above the breakpoint, so the selects rejoin the row above as ordinary flex items rather than sitting in a nested box */}
          <div
            id={`${searchId}-filters`}
            className={`w-full flex-col gap-3 ${filtersOpen ? "flex" : "hidden"} sm:contents`}
          >
            {mobileControls && <div className="flex flex-col gap-3 sm:hidden">{mobileControls}</div>}

            {filters.map(({ id, label, value, onChange, options, allLabel = "All", widthClassName = "sm:w-48" }) => (
              <div key={id} className={`w-full ${widthClassName}`}>
                <Select
                  id={id}
                  label={label}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  options={[{ value: "", label: allLabel }, ...options]}
                />
              </div>
            ))}

            {hasSort && (
              <div className="w-full sm:w-48">
                <Select
                  id={`${searchId}-sort`}
                  label="Sort"
                  value={sortBy}
                  onChange={(e) => onSortChange?.(e.target.value)}
                  options={sortOptions}
                />
              </div>
            )}

            {/* Always rendered, only disabled — appearing on first keystroke would resize the search field and shove every select sideways mid-type. */}
            <button
              type="button"
              disabled={!canClear}
              onClick={handleClear}
              className="eai-btn-outline w-full sm:w-auto"
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Placeholder matching FilterBar's layout — keep the two in sync when the bar changes.
export const FilterBarSkeleton = ({
  filters = 2,
  className = "",
}: {
  filters?: number;
  className?: string;
}) => (
  <div className={`eai-gap-toolbar ${className}`}>
    <div className="eai-panel">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-0 flex-1 basis-48">
          <Skeleton className="mb-1.5 h-3 w-16" />
          <Skeleton className="h-eai-control-lg w-full" radius="md" />
        </div>
        {/* Mirrors the bar's own breakpoint: one opener below sm, the selects themselves above it */}
        {filters > 0 && <Skeleton className="h-eai-control-lg w-28 shrink-0 sm:hidden" radius="md" />}
        <div className="hidden gap-3 sm:contents">
          {Array.from({ length: filters }).map((_, i) => (
            <div key={i} className="hidden w-full sm:block sm:w-48">
              <Skeleton className="mb-1.5 h-3 w-20" />
              <Skeleton className="h-eai-control-lg w-full" radius="md" />
            </div>
          ))}
          <Skeleton className="hidden h-eai-control-lg w-20 sm:block" radius="md" />
        </div>
      </div>
    </div>
  </div>
);

export default FilterBar;
